import {
  ChevronDown, Flag, MoreHorizontal, Phone, Plus, Repeat, Settings2, Trash2, UserPlus, X,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { phaseColor } from "@/features/cadence-preview";
import {
  CountPicker, DayPicker, OptionPicker, PriorityPicker, TextPicker, Token, WaitPicker,
  type Option, type Rung,
} from "@/features/cadence-tokens";
import {
  CADENCE_BOUNDS, strategiesFor, waitsFor, withCall, withoutCall, type CadenceFormValues,
  type PhaseValues,
} from "@/lib/cadence";
import {
  DAY_PRESETS, WAIT_PRESETS, phaseDays, phaseEndBounds, phaseFromDay, phaseNameProblem, waitWords,
  withPhase, withoutPhase,
} from "@/lib/cadence-plan";
import { cn } from "@/lib/utils";

/**
 * A cadence as a flow a lead goes down (ADR-066): they join, each phase's calls come in a cycle
 * that repeats until the phase's last day, the next phase takes over, and the cadence stops. Every
 * number is a word in its sentence that opens a picker; calls and phases are added and taken
 * away in place. The rules every phase shares fold away at the bottom.
 */

type Set = (next: CadenceFormValues | ((v: CadenceFormValues) => CadenceFormValues), key?: string) => void;

const STRATEGIES: readonly Option<string>[] = [
  { value: "branded", label: "The branded number", description: "The campaign's own caller ID." },
  { value: "same_as_last", label: "The same number as the last call", description: "So a callback reaches the same place." },
  {
    value: "unbranded",
    label: "A local number",
    description: "Another number routed to this campaign, in the lead's state when there is one (ADR-069).",
  },
  {
    value: "rotate",
    label: "A rotating number",
    description: "The campaign's numbers in turn, the one used longest ago first, never the number the lead just heard (ADR-075).",
  },
];

const STRATEGY_WORDS: Record<string, string> = {
  branded: "the branded number",
  same_as_last: "the same number",
  unbranded: "a local number",
  rotate: "a rotating number",
};

const COUNTS: readonly Option<"yes" | "no">[] = [
  { value: "yes", label: "Counts as an attempt" },
  { value: "no", label: "Does not count" },
];

export function CadenceBuilder({
  values, set, readOnly, problems,
}: {
  values: CadenceFormValues;
  set: Set;
  readOnly: boolean;
  problems: Record<string, string>;
}) {
  const patch = (i: number, p: Partial<PhaseValues>, key?: string) =>
    set((v) => ({ ...v, phases: v.phases.map((x, j) => (j === i ? { ...x, ...p } : x)) }), key);
  const replace = (i: number, next: PhaseValues) =>
    set((v) => ({ ...v, phases: v.phases.map((x, j) => (j === i ? next : x)) }));
  const first = values.phases[0];
  const cap = values.daily_calls_per_number.trim();

  return (
    <div className="mx-auto w-full max-w-2xl">
      {problems.phases && <p className="mb-2 text-xs text-danger-11">{problems.phases}</p>}

      <Card icon={UserPlus} tone="start" title="A lead joins the campaign">
        {first && (
          <p>
            The first call goes out{" "}
            <Token
              label="First call"
              readOnly={readOnly}
              invalid={problems["phases.0.initial_delay_minutes"]}
              title="When the first call goes out"
              hint="After the lead joins a list of a campaign that runs this cadence."
              content={(close) => (
                <WaitPicker
                  value={first.initial_delay_minutes}
                  presets={WAIT_PRESETS.firstCall}
                  min={CADENCE_BOUNDS.initial_delay_minutes[0]}
                  max={CADENCE_BOUNDS.initial_delay_minutes[1]}
                  onChange={(m) => patch(0, { initial_delay_minutes: m })}
                  close={close}
                />
              )}
            >
              {waitWords(first.initial_delay_minutes)}
            </Token>
            {first.initial_delay_minutes > 0 ? " after they join." : "."}
          </p>
        )}
      </Card>

      {values.phases.map((phase, i) => (
        <React.Fragment key={i}>
          <Connector label={i === 0 ? undefined : `from day ${phaseFromDay(values, i)}`} />
          <PhaseCard
            index={i}
            values={values}
            phase={phase}
            readOnly={readOnly}
            problems={problems}
            patch={(p, key) => patch(i, p, key)}
            replace={(next) => replace(i, next)}
            remove={() => set((v) => withoutPhase(v, i))}
          />
        </React.Fragment>
      ))}

      <Connector>
        {!readOnly && values.phases.length < CADENCE_BOUNDS.phases[1] && (
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => set((v) => withPhase(v))}>
            <Plus />
            Add a phase
          </Button>
        )}
      </Connector>

      <Card icon={Flag} tone="end" title="The cadence stops">
        <p>
          After{" "}
          <Token
            label="Calls in all"
            readOnly={readOnly}
            invalid={problems.total_attempts}
            title="Calls in all"
            hint="Calls that count: our own failures do not."
            content={(close) => (
              <CountPicker
                value={values.total_attempts}
                min={CADENCE_BOUNDS.total_attempts[0]}
                max={CADENCE_BOUNDS.total_attempts[1]}
                presets={[10, 20, 30, 40, 60, 80, 100]}
                unit="calls"
                onChange={(n) => set((v) => ({ ...v, total_attempts: n ?? v.total_attempts }), "total")}
                close={close}
              />
            )}
          >
            {`${values.total_attempts} calls`}
          </Token>{" "}
          in all,{" "}
          {values.expire_days.trim() ? "or on " : "with "}
          <Token
            label="Last day"
            readOnly={readOnly}
            invalid={problems.expire_days}
            title="The last day"
            hint="Counted from when the lead joined."
            content={(close) => (
              <DayPicker
                value={values.expire_days}
                presets={DAY_PRESETS}
                min={CADENCE_BOUNDS.expire_days[0]}
                max={CADENCE_BOUNDS.expire_days[1]}
                openLabel="No end day"
                onChange={(d) => set((v) => ({ ...v, expire_days: d }))}
                close={close}
              />
            )}
          >
            {values.expire_days.trim() ? `day ${values.expire_days.trim()}` : "no end day"}
          </Token>
          {values.expire_days.trim() ? ", whichever comes first." : "."}
        </p>
        <p>
          {cap ? "Never more than " : "No limit on "}
          <Token
            label="Calls to one number in a day"
            readOnly={readOnly}
            invalid={problems.daily_calls_per_number}
            title="Calls to one number in a day"
            hint="On the lead's clock, counting every campaign and a rep's hand dial."
            content={(close) => (
              <CountPicker
                value={cap ? Number(cap) : null}
                min={CADENCE_BOUNDS.daily_calls_per_number[0]}
                max={CADENCE_BOUNDS.daily_calls_per_number[1]}
                presets={[1, 2, 3, 4, 5, 6]}
                noneLabel="No cap"
                unit="a day"
                onChange={(n) => set((v) => ({ ...v, daily_calls_per_number: n === null ? "" : String(n) }), "cap")}
                close={close}
              />
            )}
          >
            {cap ? `${cap} call${cap === "1" ? "" : "s"}` : "calls"}
          </Token>{" "}
          to one number in a day{cap ? ", from any campaign." : "."}
        </p>
      </Card>

      <Rules values={values} set={set} readOnly={readOnly} problems={problems} />
    </div>
  );
}

// --- a phase ----------------------------------------------------------------------------------------

function PhaseCard({
  index, values, phase, readOnly, problems, patch, replace, remove,
}: {
  index: number;
  values: CadenceFormValues;
  phase: PhaseValues;
  readOnly: boolean;
  problems: Record<string, string>;
  patch: (p: Partial<PhaseValues>, key?: string) => void;
  replace: (next: PhaseValues) => void;
  remove: () => void;
}) {
  const at = (f: string) => problems[`phases.${index}.${f}`];
  const calls = Number.isFinite(phase.attempts) ? Math.max(1, Math.min(phase.attempts, CADENCE_BOUNDS.attempts[1])) : 1;
  const waits = waitsFor(calls, phase.between_minutes);
  const strategies = strategiesFor(calls, phase.did_strategy);
  const bounds = phaseEndBounds(values, index);
  const open = phase.ends_days.trim() === "";
  const others: Rung[] = [
    { label: "A promised callback", value: values.priority_callback },
    { label: "A lead near signing", value: values.priority_about_to_sign },
    { label: "Second call of a double dial", value: values.priority_mid_cycle },
    { label: "A lead never reached", value: values.priority_initial_fresh },
    ...values.phases.filter((_, j) => j !== index).map((p) => ({ label: `Phase “${p.name}”`, value: p.priority_band })),
  ];
  const callIssues = [at("attempts"), at("between_minutes"), at("did_strategy")].filter(Boolean);

  return (
    <article
      aria-label={`Phase ${index + 1}, ${phaseDays(values, index)}`}
      className={cn(
        "relative overflow-hidden rounded-xl border bg-surface shadow-xs",
        Object.keys(problems).some((k) => k.startsWith(`phases.${index}.`)) ? "border-danger-9" : "border-border",
      )}
    >
      <span className={cn("absolute inset-y-0 left-0 w-1", phaseColor(index))} aria-hidden />
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border py-2.5 pl-5 pr-3">
        <h3 className="text-sm font-semibold text-text">{phaseDays(values, index)}</h3>
        <Token
          label="Phase name"
          text={phase.name}
          tone="muted"
          readOnly={readOnly}
          invalid={at("name")}
          title="Phase name"
          hint="It names the phase in a lead's history and the priority reasons."
          content={(close) => (
            <TextPicker
              label="Phase name"
              mono
              value={phase.name}
              validate={(n) => phaseNameProblem(values, index, n)}
              onChange={(name) => patch({ name })}
              close={close}
            />
          )}
        >
          <span className="font-mono text-xs">{phase.name}</span>
        </Token>
        <span className="ml-auto flex items-center gap-1">
        <Token
          label="Priority"
          text={`${phase.priority_band}${phase.age_decay_per_day > 0 ? `, dropping ${phase.age_decay_per_day} a day` : ""}`}
          readOnly={readOnly}
          invalid={at("priority_band") ?? at("age_decay_per_day")}
          title="Who is called first"
          hint="When there are more leads due than reps free."
          content={() => (
            <PriorityPicker
              band={phase.priority_band}
              decay={phase.age_decay_per_day}
              others={others}
              onChange={(band, decay) => patch({ priority_band: band, age_decay_per_day: decay }, `priority-${index}`)}
            />
          )}
        >
          <span className="tabular-nums">
            <span className="font-normal opacity-80">priority </span>
            {phase.priority_band}
            {phase.age_decay_per_day > 0 && <span className="font-normal opacity-80"> −{phase.age_decay_per_day}/day</span>}
          </span>
        </Token>
        {!readOnly && values.phases.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={`More for phase ${index + 1}`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem destructive onSelect={remove}>
                <Trash2 />
                Remove this phase
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </span>
      </header>

      <ol className="space-y-0 py-3 pl-5 pr-3 text-sm text-text-muted">
        {Array.from({ length: calls }, (_, k) => (
          <React.Fragment key={k}>
            {k > 0 && (
              <li className="relative flex items-center gap-2 py-1 pl-8">
                <Rail />
                <span>
                  {waits[k - 1] === 0 ? "then " : "then, "}
                  <Token
                    label={`Wait before call ${k + 1}`}
                    readOnly={readOnly}
                    invalid={at("between_minutes")}
                    title={`Before call ${k + 1}`}
                    hint="From the end of the call before it. Right away goes out on the dialer's next pick."
                    content={(close) => (
                      <WaitPicker
                        value={waits[k - 1] ?? 1}
                        presets={WAIT_PRESETS.between}
                        min={CADENCE_BOUNDS.between_minutes[0]}
                        max={CADENCE_BOUNDS.between_minutes[1]}
                        onChange={(m) => patch({ between_minutes: waits.map((x, j) => (j === k - 1 ? m : x)) })}
                        close={close}
                      />
                    )}
                  >
                    {waitWords(waits[k - 1] ?? 1)}
                  </Token>
                  {waits[k - 1] === 0 ? "" : " later"}
                </span>
              </li>
            )}
            <li className="group relative flex items-center gap-2 py-1.5">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-3 text-accent-11" aria-hidden>
                <Phone className="size-3" />
              </span>
              <span className="min-w-0 pl-2">
                <span className="font-medium text-text">Call {k + 1}</span> from{" "}
                <Token
                  label={`Number for call ${k + 1}`}
                  readOnly={readOnly}
                  invalid={at("did_strategy")}
                  title={`Call ${k + 1} comes from`}
                  width="w-80"
                  content={(close) => (
                    <OptionPicker
                      value={strategies[k] ?? "branded"}
                      options={STRATEGIES}
                      onChange={(s) => patch({ did_strategy: strategies.map((x, j) => (j === k ? s : x)) })}
                      close={close}
                    />
                  )}
                >
                  {STRATEGY_WORDS[strategies[k] ?? "branded"] ?? strategies[k]}
                </Token>
              </span>
              {!readOnly && calls > 1 && (
                <button
                  type="button"
                  aria-label={`Remove call ${k + 1}`}
                  onClick={() => replace(withoutCall(phase, k))}
                  className="ml-auto rounded p-1 text-text-subtle opacity-0 transition-opacity hover:bg-surface-hover hover:text-text focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          </React.Fragment>
        ))}
        {!readOnly && calls < CADENCE_BOUNDS.attempts[1] && (
          <li className="relative py-1 pl-8">
            <Rail />
            <button
              type="button"
              onClick={() => replace(withCall(phase))}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-accent-11 hover:bg-accent-2"
            >
              <Plus className="size-3.5" />
              Add a call
            </button>
          </li>
        )}
        {callIssues.length > 0 && <li className="py-1 pl-8 text-xs text-danger-11">{callIssues[0]}</li>}
        <li className="flex items-start gap-2 pt-2">
          <span className="grid size-6 shrink-0 place-items-center rounded-full bg-gray-3 text-text-muted" aria-hidden>
            <Repeat className="size-3" />
          </span>
          <span className="min-w-0 pl-2 leading-6">
            {calls > 1 ? "Again " : "Again, "}
            <Token
              label="Wait before the next cycle"
              readOnly={readOnly}
              invalid={at("after_cycle_minutes")}
              title="Before the calls start over"
              hint={calls > 1 ? "From the end of the last call of the cycle." : "From the end of the call."}
              content={(close) => (
                <WaitPicker
                  value={phase.after_cycle_minutes}
                  presets={WAIT_PRESETS.cycle}
                  min={CADENCE_BOUNDS.wait_minutes[0]}
                  max={CADENCE_BOUNDS.wait_minutes[1]}
                  onChange={(m) => patch({ after_cycle_minutes: m })}
                  close={close}
                />
              )}
            >
              {waitWords(phase.after_cycle_minutes)}
            </Token>{" "}
            {calls > 1 ? "after the last call" : "later"}, until{" "}
            <Token
              label="Phase ends"
              readOnly={readOnly}
              invalid={at("ends_days")}
              title="The phase ends on"
              hint={bounds.open ? "Counted from when the lead joined. The last phase may run until the cadence stops." : "Counted from when the lead joined."}
              content={(close) => (
                <DayPicker
                  value={phase.ends_days}
                  presets={DAY_PRESETS}
                  min={bounds.min}
                  max={bounds.max}
                  openLabel={bounds.open ? "Until the cadence stops" : undefined}
                  onChange={(d) => patch({ ends_days: d })}
                  close={close}
                />
              )}
            >
              {open ? "the cadence stops" : `day ${phase.ends_days.trim()}`}
            </Token>
            .
          </span>
        </li>
      </ol>

      {calls > 1 && (
        <footer className="border-t border-border bg-surface-sunken py-2 pl-5 pr-3 text-xs text-text-muted">
          {phase.not_after_minutes.trim() ? "If a second call runs " : "A late second call goes out "}
          <Token
            label="Late second call"
            readOnly={readOnly}
            invalid={at("not_after_minutes")}
            title="A second call running late"
            hint="The calling hours or the daily cap can hold a second call. Past this, the lead starts the next cycle instead."
            content={(close) => (
              <>
                <OptionPicker
                  value={phase.not_after_minutes.trim() ? "wait" : "never"}
                  options={[{ value: "never", label: "Never drop it", description: "It goes out however late." }]}
                  onChange={() => patch({ not_after_minutes: "" })}
                  close={close}
                />
                <div className="mt-2 border-t border-border pt-2">
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-text-subtle">Drop it after</p>
                  <WaitPicker
                    value={phase.not_after_minutes.trim() ? Number(phase.not_after_minutes) : 120}
                    presets={WAIT_PRESETS.lateCall}
                    min={CADENCE_BOUNDS.wait_minutes[0]}
                    max={CADENCE_BOUNDS.wait_minutes[1]}
                    onChange={(m) => patch({ not_after_minutes: String(m) })}
                    close={close}
                  />
                </div>
              </>
            )}
          >
            {phase.not_after_minutes.trim() ? waitWords(Number(phase.not_after_minutes)) : "however late"}
          </Token>
          {phase.not_after_minutes.trim() ? " late, it is dropped and the next cycle starts." : "."}
        </footer>
      )}
    </article>
  );
}

// --- the rules every phase shares --------------------------------------------------------------------

function Rules({
  values, set, readOnly, problems,
}: {
  values: CadenceFormValues;
  set: Set;
  readOnly: boolean;
  problems: Record<string, string>;
}) {
  const keys = [
    "technical_retry_minutes", "max_retries", "invalid_stops_after", "not_worked_minutes",
    "callback_default_minutes", "priority_callback", "priority_about_to_sign", "priority_mid_cycle",
    "priority_initial_fresh",
  ];
  const troubled = keys.some((k) => problems[k]);
  const [open, setOpen] = React.useState(false);
  const shown = open || troubled;
  const put = <K extends keyof CadenceFormValues>(key: K, value: CadenceFormValues[K], coalesce?: string) =>
    set((v) => ({ ...v, [key]: value }), coalesce);
  const band = (key: "priority_callback" | "priority_about_to_sign" | "priority_mid_cycle" | "priority_initial_fresh", label: string) => (
    <Token
      label={label}
      text={String(values[key])}
      readOnly={readOnly}
      invalid={problems[key]}
      title={label}
      content={() => (
        <PriorityPicker
          band={values[key]}
          selfLabel={label}
          others={[
            { label: "A promised callback", value: values.priority_callback, key: "priority_callback" },
            { label: "A lead near signing", value: values.priority_about_to_sign, key: "priority_about_to_sign" },
            { label: "Second call of a double dial", value: values.priority_mid_cycle, key: "priority_mid_cycle" },
            { label: "A lead never reached", value: values.priority_initial_fresh, key: "priority_initial_fresh" },
            ...values.phases.map((p) => ({ label: `Phase “${p.name}”`, value: p.priority_band, key: p.name })),
          ].filter((r) => r.key !== key)}
          onChange={(n) => put(key, n, key)}
        />
      )}
    >
      <span className="tabular-nums">{values[key]}</span>
    </Token>
  );
  const counts = (key: "abandoned_counts" | "manual_dial_counts" | "inbound_counts", label: string) => (
    <Token
      label={label}
      readOnly={readOnly}
      title={label}
      content={(close) => (
        <OptionPicker value={values[key] ? "yes" : "no"} options={COUNTS} onChange={(c) => put(key, c === "yes")} close={close} />
      )}
    >
      {values[key] ? "counts" : "does not count"}
    </Token>
  );

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={shown}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover"
      >
        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-gray-3 text-text-muted" aria-hidden>
          <Settings2 className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-text">Rules for every phase</span>
          <span className="block truncate text-xs text-text-muted">
            Our failures retried {values.max_retries}× after {waitWords(values.technical_retry_minutes)} · a hang-up while
            connecting retried after {waitWords(values.not_worked_minutes)} · callbacks first ({values.priority_callback})
          </span>
        </span>
        <ChevronDown className={cn("size-4 shrink-0 text-text-subtle transition-transform", shown && "rotate-180")} aria-hidden />
      </button>
      {shown && (
        <div className="space-y-4 border-t border-border px-4 py-4 text-sm leading-7 text-text-muted">
          <Rule title="When a call fails on our side">
            A trunk or switch failure is tried again{" "}
            <Token
              label="Retry a failure after"
              readOnly={readOnly}
              invalid={problems.technical_retry_minutes}
              title="Try a failed call again after"
              content={(close) => (
                <WaitPicker
                  value={values.technical_retry_minutes}
                  presets={WAIT_PRESETS.technical}
                  min={CADENCE_BOUNDS.technical_retry_minutes[0]}
                  max={CADENCE_BOUNDS.technical_retry_minutes[1]}
                  onChange={(m) => put("technical_retry_minutes", m)}
                  close={close}
                />
              )}
            >
              {waitWords(values.technical_retry_minutes)}
            </Token>{" "}
            later, up to{" "}
            <Token
              label="Retries"
              readOnly={readOnly}
              invalid={problems.max_retries}
              title="Tries after a failure"
              content={(close) => (
                <CountPicker
                  value={values.max_retries}
                  min={CADENCE_BOUNDS.max_retries[0]}
                  max={CADENCE_BOUNDS.max_retries[1]}
                  presets={[0, 1, 2, 3, 5]}
                  unit="times"
                  onChange={(n) => put("max_retries", n ?? 0, "retries")}
                  close={close}
                />
              )}
            >
              {`${values.max_retries} time${values.max_retries === 1 ? "" : "s"}`}
            </Token>
            , and it does not count. A number the carrier says is not in service is dropped after{" "}
            <Token
              label="Invalid number, stop after"
              readOnly={readOnly}
              invalid={problems.invalid_stops_after}
              title="Not in service, in a row"
              content={(close) => (
                <CountPicker
                  value={values.invalid_stops_after}
                  min={CADENCE_BOUNDS.invalid_stops_after[0]}
                  max={CADENCE_BOUNDS.invalid_stops_after[1]}
                  presets={[1, 2, 3]}
                  unit="in a row"
                  onChange={(n) => put("invalid_stops_after", n ?? 1, "invalid")}
                  close={close}
                />
              )}
            >
              {`${values.invalid_stops_after} tr${values.invalid_stops_after === 1 ? "y" : "ies"}`}
            </Token>{" "}
            in a row.
          </Rule>
          <Rule title="Outcomes">
            A lead who hangs up while being connected is called again{" "}
            <Token
              label="Hung up while connecting, again after"
              readOnly={readOnly}
              invalid={problems.not_worked_minutes}
              title="Called again after hanging up while connecting"
              content={(close) => (
                <WaitPicker
                  value={values.not_worked_minutes}
                  presets={WAIT_PRESETS.notWorked}
                  min={CADENCE_BOUNDS.not_worked_minutes[0]}
                  max={CADENCE_BOUNDS.not_worked_minutes[1]}
                  onChange={(m) => put("not_worked_minutes", m)}
                  close={close}
                />
              )}
            >
              {waitWords(values.not_worked_minutes)}
            </Token>{" "}
            later, and it does not count. A callback promised with no time comes back{" "}
            <Token
              label="A callback with no time"
              readOnly={readOnly}
              invalid={problems.callback_default_minutes}
              title="A callback with no time"
              hint="Kept for the rep who promised it."
              content={(close) => (
                <WaitPicker
                  value={values.callback_default_minutes}
                  presets={WAIT_PRESETS.callback}
                  min={CADENCE_BOUNDS.wait_minutes[0]}
                  max={CADENCE_BOUNDS.wait_minutes[1]}
                  onChange={(m) => put("callback_default_minutes", m)}
                  close={close}
                />
              )}
            >
              {waitWords(values.callback_default_minutes)}
            </Token>{" "}
            later.
          </Rule>
          <Rule title="What counts as an attempt">
            An abandoned call (the lead answered, no rep was free) {counts("abandoned_counts", "An abandoned call")}. A rep&apos;s
            hand dial through the campaign {counts("manual_dial_counts", "A rep's hand dial")}. A call the lead makes to us{" "}
            {counts("inbound_counts", "A call the lead makes")}.
          </Rule>
          <Rule title="Who is called first when reps are short">
            A promised callback {band("priority_callback", "A promised callback")}, a lead near signing{" "}
            {band("priority_about_to_sign", "A lead near signing")}, the second call of a double dial{" "}
            {band("priority_mid_cycle", "Second call of a double dial")}, a lead never reached{" "}
            {band("priority_initial_fresh", "A lead never reached")}. Everyone else takes their phase&apos;s priority. Higher
            goes first; the calling hours, a hold and the daily cap always come before priority.
          </Rule>
        </div>
      )}
    </section>
  );
}

function Rule({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{title}</p>
      <p>{children}</p>
    </div>
  );
}

// --- the flow's pieces -------------------------------------------------------------------------------

function Card({
  icon: Icon, tone, title, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: "start" | "end";
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article className="flex gap-3 rounded-xl border border-border bg-surface px-4 py-3 shadow-xs">
      <span
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-lg",
          tone === "start" ? "bg-accent-9 text-white dark:text-gray-1" : "bg-gray-12 text-gray-1 dark:bg-gray-11",
        )}
        aria-hidden
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 space-y-1 text-sm leading-7 text-text-muted">
        <h3 className="text-sm font-semibold leading-8 text-text">{title}</h3>
        {children}
      </div>
    </article>
  );
}

function Connector({ label, children }: { label?: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center">
      <span className="h-3 w-px bg-border-strong" aria-hidden />
      {label && (
        <span className="rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-[11px] font-medium text-text-muted">
          {label}
        </span>
      )}
      {children}
      <span className="h-3 w-px bg-border-strong" aria-hidden />
    </div>
  );
}

/** The dotted line between two calls of a cycle. */
function Rail() {
  return <span className="absolute bottom-0 left-3 top-0 w-px border-l border-dashed border-border-strong" aria-hidden />;
}
